import { useState, useEffect, useCallback, Fragment, useMemo, useRef, useContext, Component } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Use unified date utils (configured timezone)
import { moment, SYSTEM_TIMEZONE } from '../utils/dateUtils';

import { Box, Button, Typography, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

// Custom Components
import { Spinner, BrowserTitle, DateSwitcher, Calendar } from '../components';
import TimeBlockModal from '../components/Appointments/TimeBlockModal';
import AddressSearchModal from '../components/Appointments/AddressSearchModal';
import ViewSwitcher from '../components/Appointments/ViewSwitcher';
import WeekView from '../components/Appointments/WeekView';
import MonthView from '../components/Appointments/MonthView';

// Utils
import messages from '../static/messages';

// Services
import appointmentsServices from '../services/appointmentsServices';
import userServices from '../services/userServices';
import settingsServices from '../services/settingsServices';
import integrationServices from '../services/integrationServices';

// WebSocket for real-time updates
import { useAppointmentSocket } from '../hooks/useSocket';

// Auth context
import { AuthContext } from '../context/AuthProvider';

// Styles
import { button } from '../static/theme-styles';

// External variables
import { defineTime } from '../components/Appointments/Calendar/utils';

export const Appointments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize date from URL, sessionStorage, or default to today
  const getInitialDate = () => {
    // First try URL
    const urlDate = searchParams.get('date');
    if (urlDate && moment(urlDate).isValid()) {
      return moment(urlDate).format();
    }
    // Then try sessionStorage
    const savedDate = sessionStorage.getItem('appointments_date');
    if (savedDate && moment(savedDate).isValid()) {
      return moment(savedDate).format();
    }
    // Default to today
    return moment().format();
  };
  
  const getInitialView = () => {
    const urlView = searchParams.get('view');
    if (urlView && ['day', 'week', 'month'].includes(urlView)) {
      return urlView;
    }
    const savedView = sessionStorage.getItem('appointments_view');
    if (savedView && ['day', 'week', 'month'].includes(savedView)) {
      return savedView;
    }
    return 'day';
  };
  
  const [date, setDateState] = useState(getInitialDate);
  const [view, setViewState] = useState(getInitialView);
  
  // Wrapper for setDate that also updates URL and sessionStorage
  const setDate = useCallback((newDate) => {
    setDateState(newDate);
    const dateStr = moment(newDate).format('YYYY-MM-DD');
    sessionStorage.setItem('appointments_date', dateStr);
    setSearchParams(prev => {
      prev.set('date', dateStr);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);
  
  // Wrapper for setView that also updates URL and sessionStorage
  const setView = useCallback((newView) => {
    setViewState(newView);
    sessionStorage.setItem('appointments_view', newView);
    setSearchParams(prev => {
      prev.set('view', newView);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);
  const [appointments, setAppointments] = useState(null);
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [queue, setQueue] = useState([]);
  const [techniciansList, setTechniciansList] = useState(null);
  const [timeBlockModal, setTimeBlockModal] = useState(false);
  const [editBlockInfo, setEditBlockInfo] = useState(null);
  const [addressSearchOpen, setAddressSearchOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Work hours settings
  const [workHours, setWorkHours] = useState({ startHour: 7, endHour: 22, workDays: [1, 2, 3, 4, 5, 6] });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // Initialization state - CRITICAL for reliable rendering
  const [isInitialized, setIsInitialized] = useState(false);
  const initializingRef = useRef(false);
  
  // Delivery integration
  const [technicianUnavailability, setTechnicianUnavailability] = useState({});
  
  // Column order (saved settings)
  const [columnOrder, setColumnOrder] = useState([]);

  const navigate = useNavigate();
  // moment.tz.setDefault is now configured in dateUtils.js
  
  // Get user data for role check
  const { userData } = useContext(AuthContext);
  const isSuperAdmin = userData?.role === 'superadmin';

  // Calculate number of time slots based on work hours
  const numSlots = useMemo(() => {
    return workHours.endHour - workHours.startHour + 1;
  }, [workHours]);

  // Action functions
  const onAddAppointment = () => navigate('/appointments/create');

  // Build base columns structure (without appointments) - MEMOIZED
  const baseColumns = useMemo(() => {
    if (!techniciansList) return null;
    
    let dynamicColumns = techniciansList.map((tech) => ({
      info: tech,
      items: Array.from(Array(numSlots), (_, index) => ({
        dump: true,
        time: defineTime(index, workHours.startHour),
      })),
    }));
    
    // Apply saved column order if available
    if (columnOrder && columnOrder.length > 0) {
      dynamicColumns.sort((a, b) => {
        const indexA = columnOrder.indexOf(a.info.tech_id);
        const indexB = columnOrder.indexOf(b.info.tech_id);
        if (indexA === -1 && indexB === -1) {
          if (a.info.isMainDispatcher) return -1;
          if (b.info.isMainDispatcher) return 1;
          return 0;
        }
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    } else {
      dynamicColumns.sort(function (a, b) {
        if (a.info.isMainDispatcher) return -1;
        if (b.info.isMainDispatcher) return 1;
        if (a.info.createdAt < b.info.createdAt) return -1;
        if (a.info.createdAt > b.info.createdAt) return 1;
        return 0;
      });
    }

    const queueColumn = {
      info: { name: 'Queue', color: '#808080' },
    };


    return [...dynamicColumns, queueColumn];
  }, [techniciansList, numSlots, workHours.startHour, columnOrder]);

  // Handle column reorder
  const handleColumnReorder = useCallback(async (newOrder) => {
    try {
      await settingsServices.updateColumnOrder(newOrder);
      setColumnOrder(newOrder);
      toast.success('Column order saved');
    } catch (error) {
      console.error('Failed to save column order:', error);
      toast.error('Failed to save column order');
    }
  }, []);

  // Handle month view day click
  const handleMonthDayClick = (clickedDate) => {
    setDate(moment(clickedDate).format());
    setView('day');
  };

  // Initialize all required data in PARALLEL
  const initialize = useCallback(async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    
    console.log('[Appointments] Starting initialization...');
    
    try {
      // PARALLEL: Load work hours, appointment types, column order simultaneously
      const [workHoursRes, typesRes, columnOrderRes] = await Promise.all([
        settingsServices.getWorkHours().catch(() => null),
        appointmentsServices.getAppointmentTypes(false).catch(() => null),
        settingsServices.getColumnOrder().catch(() => null),
      ]);
      
      const loadedWorkHours = workHoursRes?.data?.value || { startHour: 7, endHour: 22, workDays: [1, 2, 3, 4, 5, 6] };
      setWorkHours(loadedWorkHours);
      
      if (typesRes?.status === 206) {
        setAppointmentTypes(typesRes.data.data);
      }
      
      if (columnOrderRes?.data?.order) {
        setColumnOrder(columnOrderRes.data.order);
      }
      
      // Load technicians list
      const techResponse = await userServices.getUserByRole('technician');
      let technicians = [];
      
      if (techResponse?.status === 206) {
        technicians = techResponse.data.data.map((tech) => ({
          tech_id: tech.id,
          name: `${tech.firstName} ${tech.lastName}`,
          color: tech.preferences?.color || 'red',
          photo: tech.photo?.url,
          createdAt: tech.createdAt,
          email: tech.email, // Store email for Delivery mapping
        }));

        // Get main dispatcher
        try {
          const mainDispatcherResponse = await userServices.getMainDispatcher();
          if (mainDispatcherResponse.status === 206 && mainDispatcherResponse.data.data?.[0]) {
            const mainDispatcher = mainDispatcherResponse.data.data[0];
            const existingIndex = technicians.findIndex(t => t.tech_id === mainDispatcher.id);
            if (existingIndex >= 0) {
              technicians.splice(existingIndex, 1);
            }
            technicians.unshift({
              tech_id: mainDispatcher.id,
              name: `${mainDispatcher.firstName} ${mainDispatcher.lastName}`,
              color: mainDispatcher.preferences?.color || 'green',
              photo: mainDispatcher.photo?.url,
              isMainDispatcher: true,
              email: mainDispatcher.email,
            });
          }
        } catch (e) {
          console.log('Main dispatcher endpoint not available');
        }
      }
      
      setTechniciansList(technicians);
      
      // Load Delivery unavailability (in background, non-blocking)
      loadDeliveryUnavailability(technicians);
      
      console.log('[Appointments] Initialization complete');
      setIsInitialized(true);
      
    } catch (error) {
      console.error('[Appointments] Initialization error:', error);
      toast.error(messages.errors.error_data_loaning);
      setIsInitialized(true); // Allow rendering even on error
    }
  }, []);

  // Load appointments for current view
  const loadAppointments = useCallback(async () => {
    if (!isInitialized) return;
    
    setLoading(true);
    try {
      if (view === 'day') {
        // PARALLEL: Load day appointments and queue
        const [aptsResponse, queueResponse] = await Promise.all([
          appointmentsServices.getAllAppointments(date.split('T')[0] + 'T13:00:00.000Z'),
          appointmentsServices.getAllAppointments(null, null, true),
        ]);
        
        if (aptsResponse.status === 206) {
          let appointments_la_time = aptsResponse.data.data.map((el) => ({
            ...el,
            startTime: moment(el.startTime).tz(SYSTEM_TIMEZONE).format(),
            endTime: moment(el.endTime).tz(SYSTEM_TIMEZONE).format(),
          }));

          if (typeFilter) {
            appointments_la_time = appointments_la_time.filter(apt => apt.type?.id === typeFilter);
          }
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            appointments_la_time = appointments_la_time.filter(apt => 
              apt.property?.address?.toLowerCase().includes(query) ||
              apt.property?.zipCode?.toLowerCase().includes(query) ||
              apt.client?.fullName?.toLowerCase().includes(query) ||
              apt.client?.firstName?.toLowerCase().includes(query) ||
              apt.client?.lastName?.toLowerCase().includes(query) ||
              apt.notes?.some(n => n.note?.toLowerCase().includes(query))
            );
          }
          setAppointments(appointments_la_time);
        }
        
        if (queueResponse.status === 206) {
          setQueue(queueResponse.data.data || []);
        }
        
      } else if (view === 'week') {
        const startOfWeek = moment(date).startOf('week').format('YYYY-MM-DD');
        const endOfWeek = moment(date).endOf('week').format('YYYY-MM-DD');
        
        const response = await appointmentsServices.getAppointmentsByDateRange(startOfWeek, endOfWeek);
        if (response.status === 206 || response.status === 200) {
          let appointments_la_time = response.data.data.map((el) => ({
            ...el,
            startTime: moment(el.startTime).tz(SYSTEM_TIMEZONE).format(),
            endTime: moment(el.endTime).tz(SYSTEM_TIMEZONE).format(),
          }));
          if (typeFilter) {
            appointments_la_time = appointments_la_time.filter(apt => apt.type?.id === typeFilter);
          }
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            appointments_la_time = appointments_la_time.filter(apt => 
              apt.property?.address?.toLowerCase().includes(query) ||
              apt.property?.zipCode?.toLowerCase().includes(query)
            );
          }
          setWeekAppointments(appointments_la_time);
        }
        
      } else if (view === 'month') {
        const startOfMonth = moment(date).startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment(date).endOf('month').format('YYYY-MM-DD');
        
        const response = await appointmentsServices.getAppointmentsByDateRange(startOfMonth, endOfMonth);
        if (response.status === 206 || response.status === 200) {
          let appointments_la_time = response.data.data.map((el) => ({
            ...el,
            startTime: moment(el.startTime).tz(SYSTEM_TIMEZONE).format(),
            endTime: moment(el.endTime).tz(SYSTEM_TIMEZONE).format(),
          }));
          if (typeFilter) {
            appointments_la_time = appointments_la_time.filter(apt => apt.type?.id === typeFilter);
          }
          setMonthAppointments(appointments_la_time);
        }
      }
    } catch (error) {
      console.error('[Appointments] Load error:', error);
      toast.error(messages.errors.error_data_loaning);
    } finally {
      setLoading(false);
    }
  }, [isInitialized, date, view, typeFilter, searchQuery]);

  // Optimized Delivery unavailability loading
  const loadDeliveryUnavailability = async (crmTechnicians) => {
    if (!crmTechnicians || crmTechnicians.length === 0) return;
    
    try {
      // PARALLEL: Get all Delivery data at once
      const [usersResponse, techResponse, unavailResponse] = await Promise.all([
        integrationServices.getDeliveryUsers().catch(() => ({ data: [] })),
        integrationServices.getDeliveryTechnicians().catch(() => ({ data: [] })),
        integrationServices.getDeliveryUnavailability().catch(() => ({ data: [] })),
      ]);
      
      const deliveryUsers = usersResponse.data || [];
      const deliveryTechs = techResponse.data || [];
      const allUnavailability = unavailResponse.data || [];

      // Build email maps
      const emailToUserId = {};
      deliveryUsers.forEach(du => {
        if (du.email) emailToUserId[du.email.toLowerCase()] = du.id;
      });
      
      const emailToTechId = {};
      deliveryTechs.forEach(dt => {
        if (dt.email) emailToTechId[dt.email.toLowerCase()] = dt.id;
      });

      // Build mapping using stored emails (no additional API calls!)
      const mapping = {};
      for (const crmTech of crmTechnicians) {
        const crmEmail = crmTech.email?.toLowerCase();
        if (!crmEmail) continue;
        
        const deliveryUserId = emailToUserId[crmEmail];
        const deliveryTechId = emailToTechId[crmEmail];
        
        const userUnavail = allUnavailability.filter(u => {
          // Prefer email match when Delivery resolves it (reliable across tables)
          if (u.user_email) {
            return u.user_email.toLowerCase() === crmEmail;
          }
          if (u.user_type === 'salesman' || u.user_type === 'technician') {
            return deliveryTechId && u.user_id === deliveryTechId;
          }
          // Helpers live in their own ID namespace (helpers table) and are not
          // CRM technicians; matching them against users ids caused false
          // "blocked" days for technicians with a colliding id.
          if (u.user_type === 'helper') {
            return false;
          }
          return deliveryUserId && u.user_id === deliveryUserId;
        });
        
        if (userUnavail.length > 0) {
          mapping[crmTech.tech_id] = userUnavail;
        }
      }

      setTechnicianUnavailability(mapping);
    } catch (error) {
      console.log('Failed to load Delivery unavailability:', error);
    }
  };

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Load appointments when initialized and dependencies change
  // NOTE: Does NOT depend on baseColumns to avoid circular updates!
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Auto-refresh Delivery unavailability every 30 seconds
  useEffect(() => {
    if (!techniciansList || techniciansList.length === 0) return;
    
    const intervalId = setInterval(() => {
      loadDeliveryUnavailability(techniciansList);
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, [techniciansList]);

  // WebSocket: Real-time updates
  const handleAppointmentSocketChange = useCallback((data) => {
    console.log('[Appointments] WebSocket event:', data);
    loadAppointments();
    
    if (data.event === 'afterUpdate') {
      toast.info('📅 Calendar updated', { 
        autoClose: 2000, 
        position: 'bottom-right',
        hideProgressBar: true,
      });
    }
  }, [loadAppointments]);

  useAppointmentSocket(handleAppointmentSocketChange);

  // Date switcher mode based on view
  const dateSwitcherMode = useMemo(() => {
    switch (view) {
      case 'week': return 'week';
      case 'month': return 'month';
      default: return 'day';
    }
  }, [view]);

  // Show spinner until initialized AND have base columns
  if (!isInitialized || !baseColumns) return <Spinner />;

  return (
    <Fragment>
      <Box component="section">
        <BrowserTitle title="Appointments | Airmaxx Pro" />

        {/* Top bar */}
        <Box
          sx={{
            mt: 1,
            mb: 2,
            backgroundColor: '#F0F3F5',
            padding: '10px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, pl: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={onAddAppointment}
              sx={button('primary', 'secondary')}
            >
              Add New
            </Button>

            <Button
              variant="contained"
              endIcon={<BlockIcon />}
              onClick={() => setTimeBlockModal(true)}
              sx={{ 
                ...button('primary', 'secondary'),
                bgcolor: '#f57c00',
                '&:hover': { bgcolor: '#ef6c00' }
              }}
            >
              Block Time
            </Button>


            {/* View Cancelled - only for admin/dispatcher */}
            {['superadmin', 'admin', 'dispatcher'].includes(userData?.role) && (
              <Button
                variant="contained"
                endIcon={<CancelIcon />}
                onClick={() => navigate('/appointments/cancelled')}
                sx={{ 
                  ...button('primary', 'secondary'),
                  bgcolor: '#d32f2f',
                  '&:hover': { bgcolor: '#b71c1c' }
                }}
              >
                View Cancelled
              </Button>
            )}
            <DateSwitcher date={date} setDate={setDate} mode={dateSwitcherMode} />
            <ViewSwitcher view={view} setView={setView} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 150 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              size="small"
              variant="outlined"
              startIcon={<TravelExploreIcon />}
              onClick={() => setAddressSearchOpen(true)}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              title="Search all appointments by address (all dates)"
            >
              All Dates
            </Button>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {appointmentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(searchQuery || typeFilter) && (
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setTypeFilter('');
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {/* Calendar Views */}
        {view === 'day' && (
          <Calendar
            baseColumns={baseColumns}
            date={date}
            appointments={appointments}
            queue={queue}
            refreshAppointments={loadAppointments}
            workHours={workHours}
            loading={loading}
            technicianUnavailability={technicianUnavailability}
            isSuperAdmin={isSuperAdmin}
            onEditTimeBlock={(blockInfo) => { setEditBlockInfo(blockInfo); setTimeBlockModal(true); }}
            onColumnReorder={handleColumnReorder}
          />
        )}

        {view === 'week' && (
          <WeekView
            date={date}
            appointments={weekAppointments}
            technicians={techniciansList}
            workHours={workHours}
          />
        )}

        {view === 'month' && (
          <MonthView
            date={date}
            appointments={monthAppointments}
            technicians={techniciansList}
            onDateClick={handleMonthDayClick}
          />
        )}
      </Box>

      <TimeBlockModal
        open={timeBlockModal}
        close={() => { setTimeBlockModal(false); setEditBlockInfo(null); }}
        technicians={techniciansList}
        getAppointments={() => loadAppointments()}
        getQueue={() => {}}
        date={date}
        blockInfo={editBlockInfo}
      />

      <AddressSearchModal
        open={addressSearchOpen}
        onClose={() => setAddressSearchOpen(false)}
        initialQuery={searchQuery}
      />
    </Fragment>
  );
};

// Error Boundary to catch rendering crashes and show error instead of white screen
class AppointmentsErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[Appointments] React crash:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2 }}>
            Something went wrong on the Appointments page
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            {this.state.error?.message || 'Unknown error'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#999', whiteSpace: 'pre-wrap', textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
            {this.state.errorInfo?.componentStack}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export const AppointmentsWithErrorBoundary = () => (
  <AppointmentsErrorBoundary>
    <Appointments />
  </AppointmentsErrorBoundary>
);
